import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, Star, CalendarCheck, TrendingUp, Users } from "lucide-react";
import { useLeadModal } from "../../context/LeadModalContext";
import { scrollToId, track } from "../../lib/api";
import { ChevronMark } from "../ChevronMark";
import { EASE } from "../Reveal";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const FloatPill = ({ icon: Icon, children, className, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay, duration: 0.6, ease: EASE }}
    className={`absolute z-20 ${className}`}
  >
    <motion.div
      animate={{ y: [0, -9, 0] }}
      transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay }}
      className="flex items-center gap-2 rounded-full border border-emerald-100/15 bg-forest-ink/80 px-4 py-2.5 shadow-xl backdrop-blur-md"
    >
      <Icon className="h-4 w-4 text-gold" />
      <span className="whitespace-nowrap text-xs font-semibold text-bone">{children}</span>
    </motion.div>
  </motion.div>
);

export function Hero() {
  const { openLeadForm } = useLeadModal();
  const ref = useRef(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 16 });
  const sy = useSpring(my, { stiffness: 55, damping: 16 });
  const rotateX = useTransform(sy, [-0.5, 0.5], [7, -7]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-9, 9]);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const fgY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-center overflow-hidden bg-forest-deep pt-[72px]"
    >
      <div className="bg-noise pointer-events-none absolute inset-0 z-10" />
      <div
        className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full opacity-25"
        style={{ background: "radial-gradient(circle, rgba(212,175,55,0.35) 0%, transparent 65%)" }}
      />
      <div
        className="pointer-events-none absolute -bottom-56 -left-40 h-[620px] w-[620px] rounded-full opacity-30"
        style={{ background: "radial-gradient(circle, rgba(28,107,81,0.5) 0%, transparent 65%)" }}
      />

      <motion.div style={{ y: bgY }} className="pointer-events-none absolute inset-0 opacity-[0.06]">
        <ChevronMark light className="absolute -left-24 top-1/4 h-[420px] w-auto" />
        <ChevronMark light className="absolute -right-16 bottom-8 h-[300px] w-auto rotate-180" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-20 mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="inline-flex items-center gap-2.5 rounded-full border border-gold/25 bg-gold/10 px-4 py-2"
              data-testid="hero-eyebrow"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold-soft">
                Education &amp; Career Consulting
              </span>
            </motion.div>

            <h1 className="mt-7 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-bone sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.15}>Personalized Education</MaskedLine>
              <MaskedLine delay={0.28}>&amp; Career Guidance</MaskedLine>
              <MaskedLine delay={0.41}>
                That Gets You{" "}
                <em className="font-editorial font-medium italic text-gold">Real&nbsp;Results.</em>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
              className="mt-6 max-w-xl text-base leading-relaxed text-emerald-100/70 sm:text-lg"
              data-testid="hero-subheadline"
            >
              We help students and professionals choose the right academic and career path with
              clarity, confidence, and expert guidance.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
              className="mt-9 flex flex-col gap-3.5 sm:flex-row sm:items-center"
            >
              <button
                data-testid="hero-book-call-btn"
                onClick={() => openLeadForm("hero_section")}
                className="group relative overflow-hidden rounded-full bg-gold px-8 py-4 font-display text-base font-bold text-forest-deep transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(212,175,55,0.45)]"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Book a Free Strategy Call
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </button>
              <button
                data-testid="hero-explore-services-btn"
                onClick={() => {
                  track("cta_click", { cta: "explore_services", source: "hero_section" });
                  scrollToId("services");
                }}
                className="rounded-full border border-emerald-100/25 px-8 py-4 font-display text-base font-semibold text-bone transition-colors duration-200 hover:border-gold/50 hover:bg-emerald-100/5"
              >
                Explore Our Services
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-9 flex items-center gap-3"
              data-testid="hero-trust-row"
            >
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-gold text-gold" />
                ))}
              </div>
              <p className="text-sm text-emerald-100/60">
                Trusted by <span className="font-semibold text-bone">500+</span> students, parents &amp; professionals
              </p>
            </motion.div>
          </div>

          <div className="relative hidden lg:col-span-5 lg:block" style={{ perspective: 1100 }}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
              style={{ rotateX, rotateY, y: fgY, transformStyle: "preserve-3d" }}
              className="relative"
            >
              <div className="relative flex aspect-square items-center justify-center rounded-[2.5rem] border border-emerald-100/10 bg-gradient-to-br from-forest/60 to-forest-ink/90 shadow-[0_40px_90px_rgba(0,0,0,0.45)] backdrop-blur-sm">
                <div className="bg-noise absolute inset-0 rounded-[2.5rem]" />
                <motion.div
                  animate={{ y: [0, -14, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                  style={{ transform: "translateZ(50px)" }}
                >
                  <ChevronMark light className="h-52 w-auto drop-shadow-[0_20px_35px_rgba(0,0,0,0.4)]" />
                </motion.div>
                <p className="absolute bottom-8 font-mono text-[10px] uppercase tracking-[0.5em] text-emerald-100/40">
                  Path · Pace · Way
                </p>
              </div>

              <FloatPill icon={TrendingUp} className="-right-5 top-10" delay={1.15}>
                98% admission success
              </FloatPill>
              <FloatPill icon={Users} className="-left-7 bottom-24" delay={1.35}>
                500+ clients guided
              </FloatPill>
              <FloatPill icon={CalendarCheck} className="-bottom-5 right-10" delay={1.55}>
                Free 30-min strategy call
              </FloatPill>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-20 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
      >
        <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-emerald-100/40">Scroll</span>
        <motion.span
          animate={{ scaleY: [1, 0.4, 1], opacity: [0.8, 0.3, 0.8] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="h-10 w-px origin-top bg-gradient-to-b from-gold to-transparent"
        />
      </motion.div>
    </section>
  );
}
